import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Diocesis {
  id: string;
  nombre: string;
}

interface UseDiocesisDisponiblesResult {
  diocesis: Diocesis[];
  loading: boolean;
  error: string | null;
  diocesisSeleccionada: string;
  setDiocesisSeleccionada: (id: string) => void;
}

/**
 * Custom hook para cargar diócesis activas.
 * Acepta parámetro opcional de provinciaId para filtrar.
 * Selecciona automáticamente la primera diócesis si no hay selección previa.
 * 
 * @param provinciaId - ID opcional de provincia para filtrar diócesis
 * @returns Objeto con lista de diócesis, estado de carga, error, y diócesis seleccionada
 */
export function useDiocesisDisponibles(provinciaId?: string): UseDiocesisDisponiblesResult {
  const [diocesis, setDiocesis] = useState<Diocesis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diocesisSeleccionada, setDiocesisSeleccionada] = useState<string>('');

  useEffect(() => {
    loadDiocesis();
  }, [provinciaId]);

  const loadDiocesis = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('diocesis')
        .select('id, nombre')
        .eq('activo', true)
        .order('nombre');

      // Filtrar por provincia si se proporciona
      if (provinciaId) {
        query = query.eq('provincia_eclesiastica_id', provinciaId);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      setDiocesis(data || []);

      // Seleccionar automáticamente la primera diócesis si no hay selección previa
      if (data && data.length > 0 && !diocesisSeleccionada) {
        setDiocesisSeleccionada(data[0].id);
      }

      // Si la diócesis seleccionada ya no está en la lista (cambió el filtro), seleccionar la primera
      if (data && data.length > 0 && diocesisSeleccionada) {
        const exists = data.some(d => d.id === diocesisSeleccionada);
        if (!exists) {
          setDiocesisSeleccionada(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading diocesis:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar diócesis');
    } finally {
      setLoading(false);
    }
  };

  return {
    diocesis,
    loading,
    error,
    diocesisSeleccionada,
    setDiocesisSeleccionada,
  };
}
